// Zustand store — single source of truth for the unified canvas.
// Climatology (2a) = boxes + filters; Live (2b) = active/demo storms + bundle.

import { create } from "zustand";
import type { Box, Storm } from "../engine/types";
import type { AnalogMatchMode } from "../engine/analog";
import { currentStormId } from "../engine/liveSystems";
import {
  countQualifyingYears,
  loadOniYears,
  loadTrackDb,
  type OniYears,
} from "../data/loader";
import {
  fetchActive,
  fetchAdvisories,
  fetchDemo,
  fetchGenesis,
  fetchLogCycle,
  fetchLogIndex,
  fetchNwsAlerts,
  fetchRecon,
  fetchStorm,
  fetchStormGis,
  fetchSurge,
  SPAGHETTI_MODELS,
  type Advisory,
  type CycleSummary,
  type ReconData,
  type GenesisWatch,
  type GtwoFeature,
  type NwsAlert,
  type OfficialGis,
  type StormBundle,
  type StormRef,
} from "../data/liveApi";
import { nearestStepIndex, type FutureInfo, type FutureModel, type FutureProduct, type OceanLayer, type SurgeInfo } from "../map/overlays/providers";
import { detectLayout, type Layout, type SheetSnap } from "./layout";
import {
  loadNarrativeCards,
  loadReplay,
  loadReplayIndex,
  type NarrativeCard,
  type ReplayDoc,
  type ReplayIndexEntry,
} from "../data/replayApi";

export type BoxSlot = "A" | "B" | "C";
export type Mode = "climatology" | "live" | "explorer";

// Raster overlays (satellite + radar, see map/overlays/providers.ts). Mode-
// agnostic — layers AND toggle UI render in every mode. Live imagery, except
// radar while a replay is on screen: that shows the NEXRAD archive at the
// replay's hour.
export type SatLayer = "off" | "geocolor" | "ir" | "wv" | "vis";
export interface OverlayState {
  satellite: SatLayer;
  radar: boolean;
  opacity: number; // 0..1 raster-opacity
  motion: boolean; // animate the loop (radar + satellite on one clock)
  speed: number; // playback speed ×(0.5 | 1 | 2)
  loopMin: number; // loop length, minutes (60 | 90 | 180)
  ocean: OceanLayer; // sea-surface temperature / ocean heat content under everything
  lightning: boolean; // NLDN + GLD360 strike density
  meso: boolean; // 1-minute GOES mesoscale loop over the selected live storm
  future: boolean; // futurecast: a model's simulated IR / radar / rain along the future path
  futureProduct: FutureProduct;
  futureModel: FutureModel;
  futureLastSim: FutureModel; // the last simulating model (IR/radar return to it from WPC and NBM)
  futureChanceIn: number; // NBM's chance view: more than this many inches
}

interface FilterState {
  yearMin: number;
  yearMax: number;
  oniRange: [number, number]; // (-3,3) == no filter
  monthFilter: number[];
}

interface LiveState {
  index: StormRef[];
  indexKind: "active" | "demo";
  demoStorms: StormRef[]; // curated past storms — always available, even while a storm is live
  selectedId: string | null;
  bundle: StormBundle | null;
  loading: boolean;
  error: string | null;
  models: string[]; // selected spaghetti techs
  showAnalogs: boolean; // Feature A: historical-analog climatology heatmap
  analogTightness: number; // box-size multiplier (lower = tighter/fewer analogs)
  analogMatchMode: AnalogMatchMode; // origin (born in box A) vs transit (passed through A)
  // ensemble sources toggled ON (GEFS / ENS / AIFS / FNV3). None when a storm
  // opens: the deterministic tracks first, the spaghetti when asked for.
  shownEnsembles: string[];
  ensembleAlpha: number; // member-line intensity multiplier (0.5–3, default 1): the "how loud is the spaghetti" knob
  genesis: GenesisWatch | null; // ECMWF pre-genesis disturbance watch
  selectedGenesis: number | null; // index into genesis.watch (plots a map marker)
  showNhcOutlook: boolean; // render NHC GTWO genesis areas on the map
  showGenesisDots: boolean; // model genesis-watch dots on the map (off: busy; NHC's areas are the always-on layer)

  // OFFICIAL NHC products (cone + coastal watch/warning) — the safety layer,
  // clearly distinguished from the model/ensemble guidance. Default ON.
  officialGis: OfficialGis | null;
  showOfficial: boolean;
  // every NHC public advisory (full + intermediate) for the selected storm —
  // official wind/pressure/motion per update, oldest first (empty for invests)
  advisories: Advisory[];
  // hurricane-hunter recon (center fixes + flight tracks) for the selected
  // storm's window; shown by default whenever there is any
  recon: ReconData | null;
  showRecon: boolean;
  // NHC Potential Storm Surge Flooding map for the selected storm (official;
  // US Atlantic/Gulf threats only) — shown by default when one exists
  surge: SurgeInfo | null;
  showSurge: boolean;
  // NWS active alerts (tornado/flash-flood/hurricane warnings, CONUS)
  alerts: NwsAlert[];
  showAlerts: boolean;

  // run-to-run scrubber (advisory log): step through past guidance cycles for
  // the selected storm. A logged cycle is byte-compatible with the live bundle,
  // so scrubBundle just feeds the existing live render path.
  runScrubActive: boolean;
  cycles: CycleSummary[]; // logged cycles for the selected storm, oldest→newest
  cycleIdx: number | null; // viewed cycle (index into cycles); null = latest/live
  scrubBundle: StormBundle | null; // bundle for cycleIdx (rendered in place of bundle)
  scrubPrevBundle: StormBundle | null; // prior cycle (ghosted to show the run-to-run shift)
  cyclesLoading: boolean;

  // futurecast (PROTOTYPE): the loaded model run + the step on screen
  futureInfo: FutureInfo | null;
  futureIdx: number;
  // NHC outlook areas: the one clicked on the map (its popup), and the one the
  // futurecast plays while no storm is open
  outlookArea: GtwoFeature | null;
  futureArea: GtwoFeature | null;
}

// Narrow screens (state/layout.ts): which layout, the phone's bottom sheet,
// and the slide-in sidebar
interface UiState {
  layout: Layout;
  sheet: SheetSnap;
  menuOpen: boolean;
}

interface ExplorerFilters {
  year: number | "all";
  category: string; // "All" | "Cat 5" | ... | "TS" | "TD"
  search: string;
}

export type FootLayer = "wind" | "surge" | "tornado" | "rain";

interface ExplorerState {
  index: ReplayIndexEntry[];
  filters: ExplorerFilters;
  selectedId: string | null;
  replay: ReplayDoc | null;
  hour: number;
  footLayers: Record<FootLayer, boolean>; // footprint layer toggles
  loading: boolean;
  error: string | null;
}

// Fluid-canvas focus: click any storm (in any mode) → bring it to the forefront
// in Explorer detail, dimming the backdrop. 2004+ storms have a full replay;
// pre-2004 degrade to track-only (no fingerprint/footprints).
export interface FocusState {
  stormId: string | null;
  source: "replay" | "track" | null;
  replay: ReplayDoc | null; // 2004+ high-fidelity
  track: Storm | null; // pre-2004 track-only (from the preloaded DB)
  hour: number;
  loading: boolean;
}

interface AppState {
  // data
  tracks: Storm[];
  oniYears: OniYears;
  narratives: Record<string, NarrativeCard[]>; // storm stories (replay sidecar)
  loaded: boolean;
  loadError: string | null;

  // mode
  mode: Mode;

  // Feature B: overlay the selected live storm's tracks under the climatology boxes
  climoOverlay: boolean;

  // raster overlays (live satellite + radar)
  overlays: OverlayState;

  // climatology
  boxA: Box | null;
  boxB: Box | null;
  boxC: Box | null;
  filters: FilterState;

  // live
  live: LiveState;

  // explorer
  explorer: ExplorerState;

  // screen layout
  ui: UiState;

  // focus (fluid canvas)
  focus: FocusState;

  // Build C: a historical storm rendered ALONGSIDE the live storm (not focus/
  // replace) for side-by-side track comparison. Same shape as focus.
  compare: FocusState;

  // actions — data + climatology
  load: () => Promise<void>;
  setMode: (m: Mode) => void;
  toggleClimoOverlay: () => Promise<void>;
  addBox: (box: Box) => void;
  clearBoxes: () => void;
  setFilters: (patch: Partial<FilterState>) => void;
  oniFilter: () => { range: [number, number] | null; qualifyingYears: number | null };

  // actions — live
  loadLiveIndex: () => Promise<void>;
  refreshLive: () => Promise<void>;
  selectLiveStorm: (id: string, opts?: { keepEnsembles?: boolean }) => Promise<void>;
  clearLiveStorm: () => void; // back to the all-systems overview
  toggleModel: (tech: string) => void;
  toggleAnalogs: () => void;
  setAnalogTightness: (v: number) => void;
  toggleAnalogMatchMode: () => void;
  toggleEnsembleSource: (source: string) => void;
  setShownEnsembles: (sources: string[]) => void;
  setEnsembleAlpha: (v: number) => void;
  loadGenesis: () => Promise<void>;
  selectGenesis: (idx: number | null) => void;
  toggleNhcOutlook: () => void;
  toggleGenesisDots: () => void;
  toggleOfficial: () => void;
  loadAdvisories: (id: string) => Promise<void>;
  loadRecon: (id: string) => Promise<void>;
  toggleRecon: () => void;
  loadSurge: (id: string) => Promise<void>;
  toggleSurge: () => void;
  toggleAlerts: () => Promise<void>;
  refreshAlerts: () => Promise<void>;
  loadCycles: () => Promise<void>;
  toggleRunScrub: () => Promise<void>;
  setCycleIdx: (i: number) => Promise<void>;
  setSatellite: (s: SatLayer) => void;
  toggleRadar: () => void;
  setOverlayOpacity: (v: number) => void;
  toggleMotion: () => void;
  setOverlaySpeed: (v: number) => void;
  setLoopMin: (v: number) => void;
  setOcean: (v: OceanLayer) => void;
  toggleLightning: () => void;
  toggleMeso: () => void;
  toggleFuture: () => void;
  setFutureProduct: (p: FutureProduct) => void;
  setFutureModel: (m: FutureModel) => void;
  setFutureChanceIn: (v: number) => void;
  setFutureIdx: (i: number) => void;
  setFutureInfo: (info: FutureInfo | null) => void;
  openOutlookArea: (area: GtwoFeature) => void;
  closeOutlookArea: () => void;
  runOutlookFuturecast: () => void; // the open area's futurecast (its invest's, once NHC has made one)

  // actions — screen layout
  setLayout: (layout: Layout) => void;
  setSheet: (sheet: SheetSnap) => void;
  setMenuOpen: (open: boolean) => void;

  // actions — explorer
  loadExplorerIndex: () => Promise<void>;
  selectExplorerStorm: (id: string) => Promise<void>;
  setHour: (h: number) => void;
  setExplorerFilters: (patch: Partial<ExplorerFilters>) => void;
  toggleFoot: (key: FootLayer) => void;

  // actions — focus
  focusStorm: (stormId: string) => Promise<void>;
  clearFocus: () => void;
  setFocusHour: (h: number) => void;

  // actions — compare (Build C)
  selectCompareStorm: (stormId: string) => Promise<void>;
  setCompareHour: (h: number) => void;
  clearCompare: () => void;
}

const FULL_ONI: [number, number] = [-3, 3];

// Non-reactive cache of fetched cycle bundles (keyed storm@cycle) so scrubbing
// back and forth doesn't refetch. Bundles are large — keep them out of the store.
const cycleBundleCache = new Map<string, StormBundle>();
async function getCycleBundle(stormId: string, cycle: string): Promise<StormBundle> {
  const key = `${stormId}@${cycle}`;
  const hit = cycleBundleCache.get(key);
  if (hit) return hit;
  const b = await fetchLogCycle(stormId, cycle);
  cycleBundleCache.set(key, b);
  return b;
}

export const useStore = create<AppState>((set, get) => ({
  tracks: [],
  oniYears: {},
  narratives: {},
  loaded: false,
  loadError: null,

  // the live map is the front door; climatology + explorer are the deep dives
  mode: "live",
  climoOverlay: false,
  overlays: {
    satellite: "off", radar: false, opacity: 0.85, motion: false, speed: 1, loopMin: 90,
    ocean: "off", lightning: false, meso: false, future: false, futureProduct: "ir", futureModel: "hfsa", futureChanceIn: 3,
    futureLastSim: "hfsa",
  },

  boxA: null,
  boxB: null,
  boxC: null,
  filters: { yearMin: 1851, yearMax: 2025, oniRange: FULL_ONI, monthFilter: [] },

  live: {
    index: [],
    indexKind: "active",
    demoStorms: [],
    selectedId: null,
    bundle: null,
    loading: false,
    error: null,
    models: SPAGHETTI_MODELS.map((m) => m.tech),
    showAnalogs: false,
    analogTightness: 1.0, // calibrated for the truncated demo snapshots; user can tighten/loosen
    analogMatchMode: "origin", // default to strict origin (born-in) match
    shownEnsembles: [],
    ensembleAlpha: 1,
    genesis: null,
    selectedGenesis: null,
    showNhcOutlook: true,
    showGenesisDots: false,
    officialGis: null,
    showOfficial: true,
    advisories: [],
    recon: null,
    showRecon: true,
    surge: null,
    showSurge: true,
    alerts: [],
    showAlerts: false,
    runScrubActive: false,
    cycles: [],
    cycleIdx: null,
    scrubBundle: null,
    scrubPrevBundle: null,
    cyclesLoading: false,
    futureInfo: null,
    futureIdx: 0,
    outlookArea: null,
    futureArea: null,
  },

  explorer: {
    index: [],
    filters: { year: "all", category: "All", search: "" },
    selectedId: null,
    replay: null,
    hour: 0,
    footLayers: { wind: true, surge: true, tornado: true, rain: true },
    loading: false,
    error: null,
  },

  focus: { stormId: null, source: null, replay: null, track: null, hour: 0, loading: false },
  compare: { stormId: null, source: null, replay: null, track: null, hour: 0, loading: false },

  ui: { layout: detectLayout(), sheet: "peek", menuOpen: false },
  setLayout: (layout) => set({ ui: { ...get().ui, layout, ...(layout === "desktop" ? { menuOpen: false } : {}) } }),
  setSheet: (sheet) => set({ ui: { ...get().ui, sheet } }),
  setMenuOpen: (menuOpen) => set({ ui: { ...get().ui, menuOpen } }),

  load: async () => {
    try {
      const [tracks, oniYears] = await Promise.all([loadTrackDb(), loadOniYears()]);
      set({ tracks, oniYears, loaded: true, loadError: null });
      // storm stories: small sidecar, fire-and-forget (empty {} on any failure)
      void loadNarrativeCards().then((narratives) => set({ narratives }));
    } catch (e) {
      set({ loadError: e instanceof Error ? e.message : String(e) });
    }
  },

  setMode: (m) => set({ mode: m }),

  toggleClimoOverlay: async () => {
    const next = !get().climoOverlay;
    set({ climoOverlay: next });
    if (next) {
      if (get().live.index.length === 0) await get().loadLiveIndex();
      if (!get().live.selectedId) {
        const first = get().live.index[0];
        if (first) await get().selectLiveStorm(first.storm_id);
      }
    }
  },

  addBox: (box) => {
    const { boxA, boxB, boxC } = get();
    if (boxA == null) set({ boxA: box });
    else if (boxB == null) set({ boxB: box });
    else if (boxC == null) set({ boxC: box });
  },

  clearBoxes: () => set({ boxA: null, boxB: null, boxC: null }),

  setFilters: (patch) => set({ filters: { ...get().filters, ...patch } }),

  oniFilter: () => {
    const { filters, oniYears } = get();
    const [lo, hi] = filters.oniRange;
    if (lo === FULL_ONI[0] && hi === FULL_ONI[1]) return { range: null, qualifyingYears: null };
    return {
      range: [lo, hi],
      qualifyingYears: countQualifyingYears(oniYears, [lo, hi], filters.yearMin, filters.yearMax),
    };
  },

  loadLiveIndex: async () => {
    set({ live: { ...get().live, loading: true, error: null } });
    try {
      // fetch the curated demo list alongside the live feed so past storms stay
      // selectable even when a storm is active (shown as a separate group)
      const [act, demoRes] = await Promise.all([
        fetchActive(),
        fetchDemo().catch(() => ({ storms: [] as StormRef[] })),
      ]);
      const demoStorms = demoRes.storms;
      if (act.storms.length > 0) {
        set({
          live: {
            ...get().live, index: act.storms, indexKind: "active", demoStorms, loading: false,
            // storms came from a stale cache while NHC is unreachable
            error: act.feed_ok === false ? "NHC feed unreachable — storm list may be stale." : null,
          },
        });
      } else {
        set({
          live: {
            ...get().live, index: demoStorms, indexKind: "demo", demoStorms, loading: false,
            // an empty list is only a quiet off-season if the feed was healthy
            error: act.feed_ok === false ? "NHC feed unreachable — showing demo storms, NOT live data." : null,
          },
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // API down: fall back to demo storms but keep the failure visible so
      // nobody mistakes an archived storm for a live one.
      try {
        const demo = await fetchDemo();
        set({
          live: {
            ...get().live, index: demo.storms, indexKind: "demo", demoStorms: demo.storms, loading: false,
            error: `Live API unreachable (${msg}) — showing demo storms, NOT live data.`,
          },
        });
      } catch {
        set({ live: { ...get().live, loading: false, error: msg } });
      }
    }
  },

  refreshLive: async () => {
    void get().loadGenesis(); // genesis watch refreshes with the live poll
    void get().refreshAlerts(); // NWS alerts ride the same cadence (no-op when hidden)
    // Silent background refresh: no spinners, never clears the current
    // bundle — a failed poll just leaves the last good state in place.
    try {
      const act = await fetchActive();
      if (act.storms.length > 0) {
        set({ live: { ...get().live, index: act.storms, indexKind: "active" } });
      }
      const { selectedId, indexKind } = get().live;
      if (selectedId && indexKind === "active") {
        void get().loadAdvisories(selectedId); // new intermediates land between bundle cycles
        void get().loadRecon(selectedId); // new center fixes arrive every hour or two in flight
        void get().loadSurge(selectedId); // a new surge map comes with each full advisory
        const bundle = await fetchStorm(selectedId, SPAGHETTI_MODELS.map((m) => m.tech));
        const ref = get().live.index.find((s) => s.storm_id === selectedId);
        if (ref?.name) bundle.name = ref.name;
        if (get().live.selectedId === selectedId) {
          set({ live: { ...get().live, bundle } });
        }
      }
    } catch {
      // keep last good state; the next poll retries
    }
  },

  selectLiveStorm: async (requested, opts) => {
    // an invest NHC has since designated opens as the storm it became
    const id = currentStormId(requested, get().live.index);
    // switching storms exits any run-scrub (cycles belong to the old storm)
    // and clears the previous storm's official products; a storm opens on
    // its deterministic tracks alone (ensembles off) unless a shared link
    // says otherwise
    set({ live: {
      ...get().live, selectedId: id, loading: true, error: null, bundle: null, officialGis: null, advisories: [], recon: null, surge: null,
      runScrubActive: false, cycles: [], cycleIdx: null, scrubBundle: null, scrubPrevBundle: null,
      outlookArea: null, futureArea: null,
      ...(opts?.keepEnsembles ? {} : { shownEnsembles: [] }),
    } });
    // the advisory ledger is independent of the guidance bundle — start it now
    void get().loadAdvisories(id);
    try {
      // fetch the FULL model suite (chips filter client-side in the Canvas)
      const bundle = await fetchStorm(id, SPAGHETTI_MODELS.map((m) => m.tech));
      // B-deck carries no storm name — enrich from the index entry when available
      const ref = get().live.index.find((s) => s.storm_id === id);
      if (ref?.name) bundle.name = ref.name;
      // guard against a stale response if the user switched storms mid-flight
      if (get().live.selectedId === id) {
        set({ live: { ...get().live, bundle, loading: false } });
      }
      // populate run-history availability for the sidebar (fire-and-forget)
      void get().loadCycles();
      // recon + surge need the bundle first: a demo snapshot's window ends at its as-of
      void get().loadRecon(id);
      void get().loadSurge(id);
      // OFFICIAL cone + watches/warnings for this storm (fire-and-forget;
      // invests come back empty and render nothing)
      void fetchStormGis(id)
        .then((gis) => {
          if (get().live.selectedId === id) set({ live: { ...get().live, officialGis: gis } });
        })
        .catch(() => { /* official layer just stays absent */ });
    } catch (e) {
      set({ live: { ...get().live, loading: false, error: e instanceof Error ? e.message : String(e) } });
    }
  },

  clearLiveStorm: () => set({ live: {
    ...get().live, selectedId: null, loading: false, error: null, bundle: null, officialGis: null, advisories: [], recon: null, surge: null,
    runScrubActive: false, cycles: [], cycleIdx: null, scrubBundle: null, scrubPrevBundle: null,
    outlookArea: null, futureArea: null,
  } }),

  // Pure client-side visibility toggle: bundles are fetched with the full
  // model suite, so flipping a chip is instant and never resets scrub state.
  toggleModel: (tech) => {
    const cur = get().live.models;
    const next = cur.includes(tech) ? cur.filter((t) => t !== tech) : [...cur, tech];
    set({ live: { ...get().live, models: next } });
  },

  toggleAnalogs: () => set({ live: { ...get().live, showAnalogs: !get().live.showAnalogs } }),

  setAnalogTightness: (v) => set({ live: { ...get().live, analogTightness: v } }),

  toggleAnalogMatchMode: () =>
    set({ live: { ...get().live, analogMatchMode: get().live.analogMatchMode === "origin" ? "transit" : "origin" } }),

  toggleEnsembleSource: (source) => {
    const cur = get().live.shownEnsembles;
    const next = cur.includes(source) ? cur.filter((s) => s !== source) : [...cur, source];
    set({ live: { ...get().live, shownEnsembles: next } });
  },

  setShownEnsembles: (sources) => set({ live: { ...get().live, shownEnsembles: sources } }),

  setEnsembleAlpha: (v) => set({ live: { ...get().live, ensembleAlpha: v } }),

  loadGenesis: async () => {
    try {
      const g = await fetchGenesis();
      set({ live: { ...get().live, genesis: g } });
    } catch {
      // leave prior watch in place; next poll retries
    }
  },

  selectGenesis: (idx) => set({ live: { ...get().live, selectedGenesis: idx } }),

  toggleNhcOutlook: () => set({ live: { ...get().live, showNhcOutlook: !get().live.showNhcOutlook } }),

  toggleGenesisDots: () => set({ live: { ...get().live, showGenesisDots: !get().live.showGenesisDots } }),

  toggleOfficial: () => set({ live: { ...get().live, showOfficial: !get().live.showOfficial } }),

  loadAdvisories: async (id) => {
    try {
      const { advisories } = await fetchAdvisories(id);
      if (get().live.selectedId === id) set({ live: { ...get().live, advisories } });
    } catch {
      // keep whatever we had; the ledger is supplementary
    }
  },

  loadRecon: async (id) => {
    const { bundle, index } = get().live;
    if (!bundle || bundle.storm_id.toLowerCase() !== id.toLowerCase()) return;
    // demo snapshots: the missions flown up to the pre-impact advisory
    const until = bundle.as_of
      ? new Date(Date.parse(/[zZ]$/.test(bundle.as_of) ? bundle.as_of : `${bundle.as_of}Z`) + 3 * 3_600_000).toISOString()
      : null;
    const name = index.find((s) => s.storm_id === id)?.name ?? (bundle.name !== id.toUpperCase() ? bundle.name : null);
    try {
      const recon = await fetchRecon(id, { until, name });
      if (get().live.selectedId === id) set({ live: { ...get().live, recon } });
    } catch {
      // recon is supplementary — the storm view stands without it
    }
  },

  toggleRecon: () => set({ live: { ...get().live, showRecon: !get().live.showRecon } }),

  loadSurge: async (id) => {
    const { bundle } = get().live;
    if (!bundle || bundle.storm_id.toLowerCase() !== id.toLowerCase()) return;
    try {
      const surge = await fetchSurge(id, bundle.as_of ?? null);
      if (get().live.selectedId === id) set({ live: { ...get().live, surge } });
    } catch {
      // no map is the common case (non-US threats); the layer just stays absent
    }
  },

  toggleSurge: () => set({ live: { ...get().live, showSurge: !get().live.showSurge } }),

  toggleAlerts: async () => {
    const next = !get().live.showAlerts;
    set({ live: { ...get().live, showAlerts: next } });
    if (next && get().live.alerts.length === 0) await get().refreshAlerts();
  },

  refreshAlerts: async () => {
    if (!get().live.showAlerts) return;
    const alerts = await fetchNwsAlerts();
    set({ live: { ...get().live, alerts } });
  },

  loadCycles: async () => {
    const id = get().live.selectedId;
    if (!id) return;
    set({ live: { ...get().live, cyclesLoading: true } });
    try {
      const { cycles } = await fetchLogIndex(id);
      if (get().live.selectedId !== id) return; // storm switched mid-flight
      set({ live: { ...get().live, cycles, cyclesLoading: false } });
    } catch {
      // 404 / error → this storm has no logged history
      if (get().live.selectedId === id) {
        set({ live: { ...get().live, cycles: [], cyclesLoading: false } });
      }
    }
  },

  toggleRunScrub: async () => {
    const live = get().live;
    if (live.runScrubActive) {
      set({ live: { ...get().live, runScrubActive: false, cycleIdx: null, scrubBundle: null, scrubPrevBundle: null } });
      return;
    }
    if (live.cycles.length === 0) await get().loadCycles();
    const cycles = get().live.cycles;
    if (cycles.length === 0) return; // nothing to scrub
    set({ live: { ...get().live, runScrubActive: true } });
    await get().setCycleIdx(cycles.length - 1); // open at the newest cycle
  },

  setCycleIdx: async (i) => {
    const { selectedId, cycles } = get().live;
    if (!selectedId || i < 0 || i >= cycles.length) return;
    set({ live: { ...get().live, cycleIdx: i } });
    try {
      const bundle = await getCycleBundle(selectedId, cycles[i].cycle);
      const prev = i > 0 ? await getCycleBundle(selectedId, cycles[i - 1].cycle) : null;
      // logged bundles carry no name (B-deck) — enrich from the index ref
      const ref = get().live.index.find((s) => s.storm_id === selectedId);
      if (ref?.name) { bundle.name = ref.name; if (prev) prev.name = ref.name; }
      // guard against switching storm / leaving scrub mid-flight
      const cur = get().live;
      if (cur.selectedId === selectedId && cur.cycleIdx === i && cur.runScrubActive) {
        set({ live: { ...get().live, scrubBundle: bundle, scrubPrevBundle: prev } });
      }
    } catch {
      // keep the prior scrub view in place; the next scrub retries
    }
  },

  setSatellite: (s) => set({ overlays: { ...get().overlays, satellite: s } }),
  toggleRadar: () => set({ overlays: { ...get().overlays, radar: !get().overlays.radar } }),
  setOverlayOpacity: (v) => set({ overlays: { ...get().overlays, opacity: v } }),
  toggleMotion: () => set({ overlays: { ...get().overlays, motion: !get().overlays.motion } }),
  setOverlaySpeed: (v) => set({ overlays: { ...get().overlays, speed: v } }),
  setLoopMin: (v) => set({ overlays: { ...get().overlays, loopMin: v } }),
  setOcean: (v) => set({ overlays: { ...get().overlays, ocean: v } }),
  toggleLightning: () => set({ overlays: { ...get().overlays, lightning: !get().overlays.lightning } }),
  toggleMeso: () => set({ overlays: { ...get().overlays, meso: !get().overlays.meso } }),
  toggleFuture: () => {
    const on = !get().overlays.future;
    // turning it off ends an outlook area's futurecast (the area has nothing else open)
    set({ overlays: { ...get().overlays, future: on }, ...(on ? {} : { live: { ...get().live, futureArea: null } }) });
  },
  setFutureProduct: (p) => {
    const o = get().overlays;
    // WPC and NBM issue rainfall only: IR / radar go back to the last
    // simulating model; the chance of heavy rain is NBM's
    const rainOnly = o.futureModel === "wpc" || o.futureModel === "nbm";
    const model = p === "chance" ? "nbm" : (p === "ir" || p === "radar") && rainOnly ? o.futureLastSim : o.futureModel;
    set({ overlays: { ...o, futureProduct: p, futureModel: model } });
  },
  setFutureModel: (m) => {
    const o = get().overlays;
    const product: FutureProduct = m === "wpc" ? "rain"
      : m === "nbm" ? (o.futureProduct === "chance" ? "chance" : "rain")
        : o.futureProduct === "chance" ? "rain" : o.futureProduct;
    set({
      overlays: {
        ...o, futureModel: m, futureLastSim: m === "wpc" || m === "nbm" ? o.futureLastSim : m, futureProduct: product,
      },
    });
  },
  setFutureChanceIn: (v) => set({ overlays: { ...get().overlays, futureChanceIn: v } }),
  setFutureIdx: (i) => set({ live: { ...get().live, futureIdx: i } }),
  setFutureInfo: (info) => {
    const { futureInfo: prev, futureIdx } = get().live;
    let idx = futureIdx;
    if (info && prev && (prev.model !== info.model || prev.product !== info.product)) {
      // another model or view: the same moment on screen
      const was = prev.steps[Math.min(futureIdx, prev.steps.length - 1)];
      idx = was ? nearestStepIndex(info.steps, was.time) : 0;
    } else if (info && (!prev || prev.cycle !== info.cycle)) {
      // a newly loaded run opens at "now" (runs land hours after they start)
      const now = Date.now();
      idx = Math.max(0, info.steps.findIndex((s) => s.time >= now - 90 * 60_000));
    }
    set({ live: { ...get().live, futureInfo: info, futureIdx: info ? Math.min(idx, info.steps.length - 1) : 0 } });
  },
  openOutlookArea: (area) => set({ live: { ...get().live, outlookArea: area } }),
  closeOutlookArea: () => set({ live: { ...get().live, outlookArea: null } }),
  runOutlookFuturecast: () => {
    const { outlookArea: area, index, indexKind, selectedId } = get().live;
    if (!area) return;
    // an outlook area opens on a global model: HAFS-A only runs once NHC
    // designates a system, and the regional models only cover the US
    const o = get().overlays;
    const model: FutureModel = o.futureModel === "gfs" ? "gfs" : "ecmwf";
    set({ overlays: { ...o, future: true, futureModel: model, futureLastSim: model,
      futureProduct: o.futureProduct === "chance" ? "rain" : o.futureProduct } });
    const invest = area.invest_id && indexKind === "active" ? index.find((s) => s.storm_id === area.invest_id) : null;
    if (invest) {
      // NHC already tracks it as an invest: its futurecast, with its own path and HAFS when it runs
      if (selectedId !== invest.storm_id) void get().selectLiveStorm(invest.storm_id);
      else set({ live: { ...get().live, outlookArea: null } });
      return;
    }
    if (selectedId) get().clearLiveStorm(); // the area is what the futurecast plays now
    set({ live: { ...get().live, outlookArea: null, futureArea: area } });
  },

  loadExplorerIndex: async () => {
    if (get().explorer.index.length > 0) return;
    set({ explorer: { ...get().explorer, loading: true, error: null } });
    try {
      const index = await loadReplayIndex();
      set({ explorer: { ...get().explorer, index, loading: false } });
    } catch (e) {
      set({ explorer: { ...get().explorer, loading: false, error: e instanceof Error ? e.message : String(e) } });
    }
  },

  selectExplorerStorm: async (id) => {
    set({ explorer: { ...get().explorer, selectedId: id, loading: true, error: null, replay: null } });
    try {
      const replay = await loadReplay(id);
      if (get().explorer.selectedId === id) {
        set({ explorer: { ...get().explorer, replay, hour: replay.peak_hour_idx, loading: false } });
      }
    } catch (e) {
      set({ explorer: { ...get().explorer, loading: false, error: e instanceof Error ? e.message : String(e) } });
    }
  },

  setHour: (h) => set({ explorer: { ...get().explorer, hour: h } }),

  setExplorerFilters: (patch) =>
    set({ explorer: { ...get().explorer, filters: { ...get().explorer.filters, ...patch } } }),

  toggleFoot: (key) =>
    set({
      explorer: {
        ...get().explorer,
        footLayers: { ...get().explorer.footLayers, [key]: !get().explorer.footLayers[key] },
      },
    }),

  focusStorm: async (stormId) => {
    // 2004+ storms have a precomputed replay; pre-2004 fall back to track-only.
    if (get().explorer.index.length === 0) await get().loadExplorerIndex();
    const hasReplay = get().explorer.index.some((r) => r.storm_id === stormId);

    if (hasReplay) {
      set({ focus: { stormId, source: "replay", replay: null, track: null, hour: 0, loading: true } });
      try {
        const replay = await loadReplay(stormId);
        if (get().focus.stormId === stormId) {
          set({ focus: { stormId, source: "replay", replay, track: null, hour: replay.peak_hour_idx, loading: false } });
        }
      } catch {
        if (get().focus.stormId === stormId) set({ focus: { ...get().focus, loading: false } });
      }
      return;
    }

    // pre-2004: use the preloaded track; start at the peak-intensity fix
    const track = get().tracks.find((t) => t.storm_id === stormId) ?? null;
    let hour = 0;
    if (track) {
      let mx = -Infinity;
      track.track.forEach((p, i) => { if (p.vmax > mx) { mx = p.vmax; hour = i; } });
    }
    set({ focus: { stormId, source: track ? "track" : null, replay: null, track, hour, loading: false } });
  },

  clearFocus: () => set({ focus: { stormId: null, source: null, replay: null, track: null, hour: 0, loading: false } }),

  setFocusHour: (h) => set({ focus: { ...get().focus, hour: h } }),

  // Build C — load a historical storm to render beside the live storm. Same
  // dual loader as focusStorm (2004+ replay, pre-2004 track-only), but it does
  // NOT dim the backdrop — both storms stay fully visible.
  selectCompareStorm: async (stormId) => {
    if (get().explorer.index.length === 0) await get().loadExplorerIndex();
    const hasReplay = get().explorer.index.some((r) => r.storm_id === stormId);

    if (hasReplay) {
      set({ compare: { stormId, source: "replay", replay: null, track: null, hour: 0, loading: true } });
      try {
        const replay = await loadReplay(stormId);
        if (get().compare.stormId === stormId) {
          set({ compare: { stormId, source: "replay", replay, track: null, hour: replay.peak_hour_idx, loading: false } });
        }
      } catch {
        if (get().compare.stormId === stormId) set({ compare: { ...get().compare, loading: false } });
      }
      return;
    }

    // pre-2004: preloaded track, start at the peak-intensity fix
    const track = get().tracks.find((t) => t.storm_id === stormId) ?? null;
    let hour = 0;
    if (track) {
      let mx = -Infinity;
      track.track.forEach((p, i) => { if (p.vmax > mx) { mx = p.vmax; hour = i; } });
    }
    set({ compare: { stormId, source: track ? "track" : null, replay: null, track, hour, loading: false } });
  },

  setCompareHour: (h) => set({ compare: { ...get().compare, hour: h } }),

  clearCompare: () => set({ compare: { stormId: null, source: null, replay: null, track: null, hour: 0, loading: false } }),
}));

// Dev-only handle for debugging/E2E in the preview console.
if (import.meta.env.DEV) {
  (window as unknown as { __store?: typeof useStore }).__store = useStore;
}
